<template>
  <ListDropdown
    v-model="selectedCategories"
    multiple
    class="product-category-dropdown"
    placeholder="Kategorien"
    label="Kategorie wählen"
    include-blank="Alle Kategorien"
    :item-url="itemUrl"
    :options="options"
    :disabled="isDisabled"
    @change="change"
  />
</template>

<script setup lang="ts">
import { ListDropdown } from '@i22team/td-longtail-vue-components';
import type { OfferProductCategoryAggregation } from '#shared/types/goliath/goliath.types';
import traverseAggregations from '~/lib/aggregation-utils/traverse';

export type ProductCategoryProps = {
  value?: string[];
  aggregations?: OfferProductCategoryAggregation[];
  itemUrl?: (value: any) => string;
  disabled?: boolean;
  tree?: boolean;
};

const props = withDefaults(defineProps<ProductCategoryProps>(), {
  value: undefined,
  aggregations: undefined,
  itemUrl: undefined,
});

const emits = defineEmits<{
  (e: 'change'): void;
  (e: 'update:modelValue', categories: string[] | undefined): void;
}>();

const selectedCategories = ref<string[] | undefined>(props.value);

const rootAggregation = computed(() => props.aggregations?.find((agg) => agg.selected));

const rootCategoryPath = computed(() => rootAggregation.value?.productCategory?.path);

const optionAggregations = computed(() =>
  props.tree ? traverseAggregations(props.aggregations) : rootAggregation.value?.children || []
);

const options = computed(() => {
  if ((optionAggregations.value?.length || 0) === 0) return [];
  return optionAggregations.value.map((aggregation) => ({
    label: aggregation.productCategory.name,
    value: aggregation.productCategory.path,
    count: aggregation.offersCount,
    depth: props.tree ? aggregation.productCategory.depth : 0,
  }));
});

const isDisabled = computed(() => props.disabled || options.value.length === 0);

watch(
  () => props.value,
  (val) => {
    selectedCategories.value = val;
  }
);

const change = () => {
  // We have multiple parent-categories
  if (props.tree) {
    emits('update:modelValue', selectedCategories.value);
  } else {
    // remove root category, if selected
    const filtered = selectedCategories.value?.filter((value) => value !== rootCategoryPath.value);

    // add root category if no category is selected
    if (filtered?.length === 0 && rootCategoryPath.value) {
      filtered.push(rootCategoryPath.value);
    }
    emits('update:modelValue', filtered);
  }
  emits('change');
};
</script>
